import React, { useState, useEffect, useCallback, useImperativeHandle } from 'react';
import {
    Image,
    Keyboard, ScrollView,
    Text,
    TouchableOpacity,
    View
} from 'react-native-web';
import PropTypes from 'prop-types';

// Custom Components
import { Button, TextBox } from '../../components';
import LoadingView from '../../components/component_loading_view';

// APIs
import { getTruckListVendor } from '../../modules';

// Utils
import { showAppError } from '../../utils/util_helpers';
import { getCoverPhoto, getLogoPhoto } from '../../utils/util_truck_helpers';

// Globals
import GLOBAL from '../../configs/config_global';

function TabSearch(props, ref) {
    const { initialKeyword, onTruckSelected, onNewProfilePressed } = props;

    // region Local State
    const [currentPage, setCurrentPage] = useState(0);
    const [lastPage, setLastPage] = useState(0);
    const [totalResult, setTotalResult] = useState(0);
    const [truckList, setTruckList] = useState([]);
    const [isLoadingSearch, setIsLoadingSearch] = useState(false);
    const [keyword, setKeyword] = useState(initialKeyword);
    // endregion

    // region Function - Go Search Truck
    const goSearchTruck = useCallback((searchKeyword = keyword) => {
        if (!searchKeyword.trim()) {
            setTruckList([]);
            setCurrentPage(0);
            setLastPage(0);
            setTotalResult(0);
            return;
        }

        setIsLoadingSearch(true);
        setTruckList([]);
        setCurrentPage(0);
        setLastPage(0);
        setTotalResult(0);
        Keyboard.dismiss();

        getTruckListVendor(searchKeyword, 1, 10)
            .then((result) => {
                setIsLoadingSearch(false);
                setTruckList(result.data);
                setTotalResult(result.totalResult);
                setCurrentPage(result.currentPage);
                setLastPage(result.lastPage);
            })
            .catch((error) => {
                setIsLoadingSearch(false);
                setTruckList([]);
                showAppError(error);
            });
    }, [keyword]);
    // endregion

    // region Function - Keep Fetching For Trucks
    const keepFetchingForTrucks = useCallback(() => {
        if (currentPage >= lastPage) return;

        setIsLoadingSearch(true);
        Keyboard.dismiss();

        getTruckListVendor(keyword, currentPage + 1, 10)
            .then((result) => {
                setIsLoadingSearch(false);
                setTruckList((prev) => prev.concat(result.data));
                setTotalResult(result.totalResult);
                setCurrentPage(result.currentPage);
                setLastPage(result.lastPage);
            })
            .catch((error) => {
                setIsLoadingSearch(false);
                alert('Unable to fetch more data from server.<br/><br/>Error message: ' + error);
            });
    }, [currentPage, lastPage, keyword]);
    // endregion

    // region Render Truck Item
    const renderTruckItem = (item) => {
        const truckLogo = getLogoPhoto(item, require('../../images/image_placeholder_logo.png'));
        const truckCover = getCoverPhoto(item, require('../../images/image_placeholder_cover.png'));

        return(
            <TouchableOpacity
                key={item.id?.toString()}
                style={{ width: '40%' }}
                onPress={() => onTruckSelected(item)}>
                <View style={[{ width: '100%', backgroundColor: 'white', alignItems: 'flex-start', padding: 16, borderWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER, borderRadius: 6, marginTop: 16 }]}>
                    {/* Cover Photo */}
                    <Image
                        source={truckCover}
                        style={{ width: '100%', height: 100, borderRadius: 6, backgroundColor: GLOBAL.COLOR.BASIC.NEUTRAL_LIGHT_GREY }}
                        resizeMode={'cover'}
                    />

                    {/* Logo and Info */}
                    <View style={{ flexDirection: 'row', alignItems: 'center', width: '100%', marginTop: 12 }}>
                        {/* Truck Logo */}
                        <Image
                            source={truckLogo}
                            style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: 'gray' }}
                            resizeMode={truckLogo ? 'cover' : 'contain'}
                        />

                        {/* Truck Info */}
                        <View style={{ flex: 1, marginLeft: 16 }}>
                            {/* Truck Name */}
                            <Text style={{ fontWeight: '500', fontSize: 18 }}>
                                {item.name?.trim()}
                            </Text>

                            {/* Cuisine */}
                            {
                                item.cuisine &&
                                <Text style={{ fontSize: 14, color: GLOBAL.COLOR.BASIC.NEUTRAL_DARK_GREY, marginTop: 4 }}>
                                    {item.cuisine.filter((cuisine, index) => index < 3).map((cuisine) => cuisine.name).join(', ')}
                                </Text>
                            }
                        </View>
                    </View>

                    {/* Divider */}
                    <View style={{ alignSelf: 'stretch', height: 1, backgroundColor: GLOBAL.COLOR.DIVIDER, marginTop: 16 }} />

                    {/* Claim This Truck */}
                    <Text style={{ color: GLOBAL.COLOR.MAIN, fontSize: 16, fontWeight: '500', marginTop: 16, alignSelf: 'center', textAlign: 'center' }}>
                        Claim This Truck
                    </Text>
                </View>
            </TouchableOpacity>
        );
    };
    // endregion

    // region Expose Methods
    useImperativeHandle(
        ref,
        () => ({ goSearchTruck, setKeyword }),
        [goSearchTruck]
    )
    // endregion

    // region MAIN RENDERING
    return(
        <View key={'registerSearchTab'} style={{ flex: 1, width: '100%' }}>
            {/* Search Text Box */}
            <View key={'registerSearchBox'} style={{ width: '100%', alignItems: 'center', paddingHorizontal: 36 }}>
                {/* Search Input */}
                <TextBox
                    key={'registerSearchInput'}
                    placeholder={'Find your food truck'}
                    returnKeyType={'done'}
                    autoCapitalize={'none'}
                    autoCorrect={false}
                    editable={!isLoadingSearch}
                    onSubmitEditing={() => goSearchTruck()}
                    onChangeText={(text) => setKeyword(text)}
                    title={'Find your food truck'}
                    value={keyword}
                    isSingleInput={true}
                    style={{ width: '40%', alignSelf: 'center' }}
                    imageName={'search'}
                    imageColor={keyword.trim() ? 'black' : GLOBAL.COLOR.BASIC.NEUTRAL_LIGHT_GREY}
                    showHintButton={!!keyword.trim()}
                    hintButtonColor={GLOBAL.COLOR.BASIC.NEUTRAL_GREY}
                    hintButtonImage={'close'}
                    hintButtonOnPress={() => setKeyword('')}
                />

                {/* New Truck Profile Button */}
                {
                    currentPage > 0 &&
                    <Button
                        key={'registerCreateProfileButton'}
                        text={'Create new food truck profile'}
                        backgroundColor={'white'}
                        style={{ marginTop: 24, borderWidth: 1, borderColor: GLOBAL.COLOR.BASIC.NEUTRAL_STROKE, width: '40%', alignSelf: 'center' }}
                        image={'plus'}
                        useFeather={true}
                        textColor={GLOBAL.COLOR.BASIC.UI_BLUE}
                        textStyle={{ fontWeight: '500' }}
                        pushText={true}
                        loadingText={'GETTING STORE LINK FOR TRUCK CONSUMER'}
                        onPress={onNewProfilePressed}
                    />
                }

                {/* Search Result Count */}
                <Text style={{ fontSize: 16, lineHeight: 20, color: GLOBAL.COLOR.TEXT_GRAY, marginTop: 24, textAlign: 'center', width: '100%' }}>
                    {
                        currentPage > 0 ?
                            (
                                totalResult + ' profile' + (totalResult > 1 ? 's' : '') + ' matched your search,' +
                                (totalResult > 1 ? ' is one of them yours?' : ' is it yours?')
                            )
                            :
                            'Start typing above to find your truck profile'
                    }
                </Text>
            </View>

            {/* Truck Result List */}
            <View key={'registerResultContainer'} style={{ flex: 1, width: '100%', marginTop: 8 }}>
                {
                    currentPage > 0 ?
                        (
                            truckList.length > 0 ?
                                <ScrollView
                                    key={'registerResultContainerScrollView'}
                                    style={{ flex: 1, width: '100%' }}
                                    contentContainerStyle={{ alignItems: 'center', paddingTop: 24, paddingBottom: 128 }}>
                                    {truckList.map((item, index) => renderTruckItem(item, index))}

                                    {/* Load More */}
                                    {
                                        (currentPage < lastPage) &&
                                        <Text
                                            onPress={() => keepFetchingForTrucks()}
                                            style={{ fontWeight: 'bold', color: GLOBAL.COLOR.BASIC.UI_BLUE, marginTop: 36 }}>
                                            Load more data
                                        </Text>
                                    }
                                </ScrollView>
                                :
                                <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 48, paddingBottom: 48 }}>
                                    <Text style={{ fontSize: 13, color: GLOBAL.COLOR.TEXT_GRAY, textAlign: 'center' }}>
                                        We could not find any truck that matches your search keyword
                                    </Text>
                                </View>
                        )
                        :
                        false
                }

                {/* Gradient */}
                <Image
                    source={require('../../images/image_gradient_down.png')}
                    style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 20 }}
                />
            </View>

            {/* Loading View */}
            {isLoadingSearch && <LoadingView isAbsolute={true}/>}
        </View>
    );
    // endregion
}

TabSearch.propTypes = {
    initialKeyword: PropTypes.string,
    onTruckSelected: PropTypes.func,
    onNewProfilePressed: PropTypes.func
};

TabSearch.defaultProps = {
    initialKeyword: '',
    onTruckSelected: () => {},
    onNewProfilePressed: () => {}
};

export default React.forwardRef(TabSearch);
